import axios from 'axios';
import { getStoredUtms } from '../utils/utm';

// Global axios instance configured with base URL
export const api = axios.create({
  baseURL: 'https://ittech-api.onrender.com',
  // baseURL: 'https://rested-jolly-oriole.ngrok-free.app',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interface for checkout session response
interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

// Function to create Stripe checkout session for subscriptions.
// Always pass price_id when the product has multiple active prices — the API
// falls back to the product default_price (not prices.data[0]).
export const createStripeCheckoutSession = async (
  productId: string,
  successUrl: string,
  cancelUrl: string,
  quantity: number = 1,
  priceId?: string,
  plan?: 'pro' | 'ultra'
): Promise<CreateCheckoutSessionResponse> => {
  try {
    const response = await api.post<CreateCheckoutSessionResponse>(
      '/stripe/create-paid-checkout-session-subscription',
      {
        product_id: productId,
        ...(priceId ? { price_id: priceId } : {}),
        ...(plan ? { plan } : {}),
        quantity: quantity,
        success_url: successUrl,
        cancel_url: cancelUrl,
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

/** Email/device trial: request code — UTMs merged from session (optional keys omitted if unset). */
export const requestEmailDeviceTrial = async (body: {
  email: string;
  product_id: string;
  device_id: string;
}) => {
  return api.post('/auth/trial/request', { ...body, ...getStoredUtms() });
};

/** Email/device trial: verify code — send if UTMs were not on request or row had none yet. */
export const verifyEmailDeviceTrial = async (body: {
  email: string;
  code: string;
  product_id: string;
  device_id: string;
}) => {
  return api.post('/auth/tverify', { ...body, ...getStoredUtms() });
};

/** Stripe registration trial — UTMs stored when a new user row is created. */
export const registerStripeTrialUser = async (body: {
  email: string;
  product_id: string;
  firstName: string;
  lastName: string;
  source: string;
}) => {
  return api.post('/v1/users/register', { ...body, ...getStoredUtms() });
};

/** Extract license string from a 200 response body. */
export function pickLicenseKeyFromResponse(data: unknown): string | null {
  if (data == null) {
    return null;
  }
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }
  if (typeof data === 'object') {
    const o = data as Record<string, unknown>;
    for (const k of ['license_key', 'key', 'licenseKey', 'license']) {
      const v = o[k];
      if (typeof v === 'string' && v.trim()) {
        return v.trim();
      }
    }
    if (o.data && typeof o.data === 'object') {
      return pickLicenseKeyFromResponse(o.data);
    }
  }
  return null;
}

export type LicenseSessionPollResult =
  | { state: 'ready'; key: string }
  | { state: 'pending' };

/**
 * 200 with a license field = ready. 202 = not ready. Other status / network = keep waiting.
 */
export const pollLicenseBySession = async (
  sessionId: string
): Promise<LicenseSessionPollResult> => {
  try {
    const res = await api.get<unknown>('/api/license', {
      params: { session_id: sessionId },
      validateStatus: (status) => status === 200 || status === 202,
    });
    if (res.status === 200) {
      const key = pickLicenseKeyFromResponse(res.data);
      if (key) {
        return { state: 'ready', key };
      }
    }
  } catch {
    // non-2xx, network, etc. — keep polling until timeout
  }
  return { state: 'pending' };
};

export default api;
